import {inject, Injectable, Injector} from "@angular/core"
import {
    MaterialAssetsRenderingMaterialFragment,
    MaterialAssetsRenderingMaterialGraphGQL,
    MaterialAssetsRenderingMaterialRangeFragment,
    MaterialAssetsRenderingMaterialRangeGQL,
} from "@app/common/services/material-assets-rendering/material-assets-rendering.generated"
import {SceneManagerService} from "@app/template-editor/services/scene-manager.service"
import {CreateJobGraphData} from "@cm/job-nodes/job-nodes"
import {SceneNodes} from "@cm/template-nodes"
import {graphToJson} from "@cm/utils/graph-json"
import {fetchThrowingErrors} from "@common/helpers/api/fetch"
import {mutateThrowingErrors} from "@common/helpers/api/mutate"
import {
    CM_TO_MM,
    MAX_PIXEL_PER_MM,
    renderGraphForTemplateGraph,
    setupTemplateGraphForPlaneRender,
} from "@common/helpers/rendering/material/material-assets-rendering/common"
import {jobGraphFn_thumbnail, thumbnailGenerationParamsForMaterial} from "@common/helpers/rendering/material/material-assets-rendering/material-thumbnail"
import {jobGraphFn_shaderBall, setupTemplateGraphForShaderBallRender} from "@common/helpers/rendering/material/material-assets-rendering/shader-ball"
import {jobGraphFn_tile, tileGenerationParamsForMaterial} from "@common/helpers/rendering/material/material-assets-rendering/tileable-material"
import {JobGraphMaterial} from "@common/models/material-assets-rendering/job-graph-material"
import {CM_LOGGER_MATERIALS} from "@common/services/logging/index"
import {
    MaterialAssetsRenderingCreateJobTaskGQL,
    MaterialAssetsRenderingMaterialGQL,
} from "@common/services/material-assets-rendering/material-assets-rendering.generated"
import {NameAssetFromSchemaService} from "@common/services/name-asset-from-schema/name-asset-from-schema.service"
import {UploadGqlService} from "@common/services/upload/upload.gql.service"
import {FlatOption} from "@labels"
import {firstValueFrom, map} from "rxjs"
import {deserializeMaterialGraph} from "@cm/material-nodes"
import {DataResolverService} from "@material-editor/services/data-resolver.service"
import {RenderNodes} from "@cm/render-nodes"
import {environment} from "@environment"
import {jobGraphFn_fabricOverviewGenerate} from "@common/helpers/rendering/material/material-assets-rendering/fabric-overview"
import {MediaType} from "@cm/utils"
import {FabricOverviewGenerateTemplateType, JobType} from "@generated"

@Injectable({
    providedIn: "root",
})
export class MaterialAssetsRenderingService {
    private readonly nameAssetFromSchemaService = inject(NameAssetFromSchemaService)
    private readonly uploadGqlService = inject(UploadGqlService)
    private readonly materialAssetsRenderingMaterial = inject(MaterialAssetsRenderingMaterialGQL)
    private readonly materialAssetsRenderingMaterialGraph = inject(MaterialAssetsRenderingMaterialGraphGQL)
    private readonly materialAssetsRenderingMaterialRange = inject(MaterialAssetsRenderingMaterialRangeGQL)
    private readonly dataResolver = inject(DataResolverService)
    private readonly injector = inject(Injector)
    private readonly logger = inject(CM_LOGGER_MATERIALS)

    checkThumbnailSizeValid(resolution: number, size_in_cm: number): boolean {
        return Math.floor(resolution / size_in_cm / CM_TO_MM) < MAX_PIXEL_PER_MM
    }

    async fetchAndVerifyMaterialDetails(materialId: string): Promise<
        MaterialAssetsRenderingMaterialFragment &
            JobGraphMaterial & {
                latestCyclesRevision: {legacyId: number}
            }
    > {
        const detail = (await fetchThrowingErrors(this.materialAssetsRenderingMaterial)({materialId})).material
        if (!detail.name) throw new Error(`Missing name field for material with id: ${materialId}`)
        if (!detail.latestCyclesRevision) throw new Error(`Missing latest cycles revision field for material with id: ${materialId}`)
        if (!detail.organization) throw new Error(`Missing organization field for material with id: ${materialId}`)
        return {
            ...detail,
            latestCyclesRevision: detail.latestCyclesRevision,
            name: detail.name,
            organization: detail.organization,
        }
    }

    async generateShaderBallThumbnail(materialId: string, sceneManagerService: SceneManagerService) {
        const materialDetails = await this.fetchAndVerifyMaterialDetails(materialId)
        const jobName = `Shader ball generation for material ${materialDetails.legacyId}`
        const jobType = JobType.MaterialShaderBall

        const sceneNodesFn = () =>
            setupTemplateGraphForShaderBallRender(
                materialDetails.legacyId,
                materialDetails.latestCyclesRevision.legacyId,
                environment.rendering.shaderBall.templateId,
                this.injector,
                sceneManagerService,
            )

        const jobGraphFn = async (
            renderGraphId: number,
            materialDetails: JobGraphMaterial,
            postProcessingSettings: SceneNodes.RenderPostProcessingSettings | undefined,
        ) => jobGraphFn_shaderBall(renderGraphId, materialDetails, postProcessingSettings)

        return this.prepareAndSubmitJob(jobName, jobType, materialDetails, sceneNodesFn, undefined, jobGraphFn)
    }

    async generateThumbnail(materialId: string, samples: number, flatOption: FlatOption, sceneManagerService: SceneManagerService) {
        const materialDetails = await this.fetchAndVerifyMaterialDetails(materialId)
        const jobName = `Thumbnail generation for material ${materialDetails.legacyId}`
        const jobType = JobType.MaterialThumbnail

        const materialGraph = deserializeMaterialGraph(materialDetails.latestCyclesRevision.graphNode)

        const {offsetX, offsetY} = await thumbnailGenerationParamsForMaterial(materialGraph, this.dataResolver.resolve, this.logger)

        const defaultNameBase = `Thumbnail - ${materialDetails.name} - ${flatOption.size.toFixed(2).replace(/\./gi, ",")}cm x ${flatOption.size
            .toFixed(2)
            .replace(/\./gi, ",")}cm`
        const filenames = await this.nameAssetFromSchemaService.getMaterialThumbnailNameSet(
            materialDetails.id,
            flatOption.dataObjectAssignmentType,
            defaultNameBase,
        )

        const extensionTemplateId = materialDetails.organization.materialAssetsTemplate?.legacyId ?? environment.rendering.materialAssets.fallbackTemplateId

        const sceneNodesFn = async () =>
            setupTemplateGraphForPlaneRender(
                materialDetails.legacyId,
                materialDetails.latestCyclesRevision.legacyId,
                extensionTemplateId,
                flatOption.resolution,
                flatOption.resolution,
                samples,
                flatOption.size,
                flatOption.size,
                this.injector,
                sceneManagerService,
                offsetX,
                offsetY,
            )

        const jobGraphFn = async (
            renderGraphId: number,
            materialDetails: JobGraphMaterial,
            postProcessingSettings: SceneNodes.RenderPostProcessingSettings | undefined,
        ) => jobGraphFn_thumbnail(renderGraphId, materialDetails, flatOption.resolution, flatOption.size, filenames, postProcessingSettings)

        return this.prepareAndSubmitJob(jobName, jobType, materialDetails, sceneNodesFn, undefined, jobGraphFn)
    }

    async generateTile(materialId: string, samples: number, sceneManagerService: SceneManagerService): Promise<string> {
        const materialDetails = await this.fetchAndVerifyMaterialDetails(materialId)
        const jobName = `Tileable image generation for material ${materialDetails.legacyId}`
        const jobType = JobType.MaterialTileableImage

        const materialGraph = await firstValueFrom(
            this.materialAssetsRenderingMaterialGraph
                .fetch({legacyId: materialDetails.latestCyclesRevision.legacyId})
                .pipe(map((result) => deserializeMaterialGraph(result.data.materialRevision.graphNode))),
        )
        const {
            repeatWidth,
            repeatHeight,
            repeatWidthCm,
            repeatHeightCm,
            paddingWidth,
            paddingHeight,
            paddedWidth,
            paddedHeight,
            paddedWidthCm,
            paddedHeightCm,
            offsetX,
            offsetY,
        } = await tileGenerationParamsForMaterial(materialGraph, this.dataResolver.resolve, this.logger)

        const defaultNameBase = `Tileable image - ${materialDetails.name} - ${repeatWidthCm.toFixed(2).replace(/\./gi, ",")}cm x ${repeatHeightCm
            .toFixed(2)
            .replace(/\./gi, ",")}cm`
        const filenames = await this.nameAssetFromSchemaService.getMaterialTileableRenderNameSet(materialDetails.id, defaultNameBase)

        const extensionTemplateId = materialDetails.organization.materialAssetsTemplate?.legacyId ?? environment.rendering.materialAssets.fallbackTemplateId

        const sceneNodesFn = async () =>
            setupTemplateGraphForPlaneRender(
                materialDetails.legacyId,
                materialDetails.latestCyclesRevision.legacyId,
                extensionTemplateId,
                paddedWidth,
                paddedHeight,
                samples,
                paddedWidthCm,
                paddedHeightCm,
                this.injector,
                sceneManagerService,
                offsetX,
                offsetY,
            )

        const modifyRenderGraphFn = (render: RenderNodes.Render) => {
            render.session.options.image_filter_type = "box"
            render.session.options.image_filter_width = 1
            return render
        }

        const jobGraphFn = async (
            renderGraphId: number,
            materialDetails: JobGraphMaterial,
            postProcessingSettings: SceneNodes.RenderPostProcessingSettings | undefined,
        ) =>
            jobGraphFn_tile(
                renderGraphId,
                materialDetails,
                repeatWidthCm,
                repeatHeightCm,
                repeatWidth,
                repeatHeight,
                paddingWidth,
                paddingHeight,
                filenames,
                postProcessingSettings,
            )

        return this.prepareAndSubmitJob(jobName, jobType, materialDetails, sceneNodesFn, modifyRenderGraphFn, jobGraphFn)
    }

    async generateFabricOverview(materialId: string): Promise<string> {
        const fetchMaterialRangeAndTileableImageDataObjectId = async (
            materialId: string,
        ): Promise<{materialRange: MaterialAssetsRenderingMaterialRangeFragment; tileableImageDataObjectId: string}> => {
            const material = (await fetchThrowingErrors(this.materialAssetsRenderingMaterialRange)({materialId})).material
            const tagAssignments = material?.tagAssignments
            if (tagAssignments.length > 1) {
                this.logger.warn(`Multiple material range assignments found for material with id: ${materialId}, using the first one`)
            }
            const materialRange = tagAssignments[0]?.tag
            if (!materialRange) {
                throw new Error(`Material does not have a material range assigned. Cannot generate fabric overview.`)
            }
            const tileableImageDataObjectId = material.outputForType?.result?.thumbnail?.id ?? material.outputForType?.result?.id // use TIFF if available, fallback to EXR
            if (!tileableImageDataObjectId) {
                throw new Error(`Material does not have a tileable image output. Cannot generate fabric overview.`)
            }
            return {materialRange, tileableImageDataObjectId}
        }

        const {materialRange, tileableImageDataObjectId} = await fetchMaterialRangeAndTileableImageDataObjectId(materialId)
        if (!materialRange.overviewTemplateType) {
            throw new Error(`Assigned material range has no overview template type assigned. Cannot generate fabric overview.`)
        }
        if (!materialRange.fullWidth) {
            throw new Error(`Assigned material range has no full width assigned. Cannot generate fabric overview.`)
        }
        if (
            materialRange.overviewTemplateType === FabricOverviewGenerateTemplateType.FullWidthSlimWithRepeat &&
            (!materialRange.repeatWidth || !materialRange.repeatHeight)
        ) {
            throw new Error(`Assigned material range's overview template type requires horizontal and/or vertical repeat. Cannot generate fabric overview.`)
        }

        const materialDetails = await this.fetchAndVerifyMaterialDetails(materialId)
        const jobName = `Fabric overview generation for material ${materialDetails.legacyId}`

        const mediaType: MediaType = "image/jpeg"
        const defaultNameBase = `Fabric overview - ${materialDetails.name}`
        const filenames = await this.nameAssetFromSchemaService.getMaterialFabricOverviewNameSet(materialDetails.id, defaultNameBase, mediaType)

        const jobGraph = jobGraphFn_fabricOverviewGenerate(
            materialDetails,
            filenames,
            materialRange.overviewTemplateType,
            tileableImageDataObjectId,
            materialRange.fullWidth,
            materialRange.repeatWidth ?? undefined,
            materialRange.repeatHeight ?? undefined,
            materialRange.railroaded,
        )

        const materialAssetsRenderingCreateJobTask = this.injector.get(MaterialAssetsRenderingCreateJobTaskGQL)
        const {createJob} = await mutateThrowingErrors(materialAssetsRenderingCreateJobTask)({
            input: {
                name: jobName,
                organizationId: materialDetails.organization.id,
                graph: graphToJson(jobGraph, this.logger),
            },
        })
        return createJob.id
    }

    async prepareAndSubmitJob(
        jobName: string,
        jobType: JobType,
        materialDetails: {legacyId: number; name: string; organization: {id: string; legacyId: number}},
        sceneNodesFn: () => Promise<SceneNodes.SceneNode[]>,
        modifyRenderGraphFn: ((render: RenderNodes.Render) => RenderNodes.Render) | undefined,
        jobGraphFn: (
            renderGraphId: number,
            material: {legacyId: number; name: string; organization: {id: string; legacyId: number}},
            postProcessingSettings: SceneNodes.RenderPostProcessingSettings | undefined,
        ) => Promise<CreateJobGraphData>,
    ): Promise<string> {
        const templateGraph = await sceneNodesFn()

        const postProcessingSettings = templateGraph.find(SceneNodes.RenderPostProcessingSettings.is)

        const originalRenderGraph = await renderGraphForTemplateGraph(templateGraph, this.injector)
        const renderGraph: RenderNodes.Render = modifyRenderGraphFn ? modifyRenderGraphFn(originalRenderGraph) : originalRenderGraph

        const uploadResult = await this.uploadGqlService.createAndUploadDataObject(
            new File([JSON.stringify(graphToJson(renderGraph, this.logger))], "render.json", {type: "application/json"}),
            {
                organizationId: materialDetails.organization.id,
            },
            {showUploadToolbar: false, processUpload: true},
        )
        const jobGraph = await jobGraphFn(uploadResult.legacyId, materialDetails, postProcessingSettings)

        const materialAssetsRenderingCreateJobTask = this.injector.get(MaterialAssetsRenderingCreateJobTaskGQL)
        const {createJob} = await mutateThrowingErrors(materialAssetsRenderingCreateJobTask)({
            input: {
                jobType,
                name: jobName,
                organizationId: materialDetails.organization.id,
                graph: graphToJson(jobGraph, this.logger),
            },
        })
        return createJob.id
    }
}
